import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  ArrowLeftRight,
  Calendar,
  Download,
  Filter,
  Loader2,
  Package,
  RotateCcw,
  Search,
} from "lucide-react";
import { toast } from "sonner";

import DataSourceNotice from "@/components/DataSourceNotice";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  healthy: "bg-emerald-100 text-emerald-800 border-emerald-200",
  below_reorder: "bg-amber-100 text-amber-800 border-amber-200",
  critical: "bg-red-100 text-red-800 border-red-200",
  unclassified: "bg-slate-100 text-slate-700 border-slate-200",
};

const statusLabels = {
  healthy: "Healthy",
  below_reorder: "Below reorder",
  critical: "Critical",
  unclassified: "Not classified",
};

const sortOptions = [
  ["material", "Material"], ["description", "Description"], ["plant", "Plant"],
  ["storage_location", "Storage location"], ["quantity", "Quantity"],
  ["stock_value", "Stock value"], ["status", "Status"],
  ["abc_classification", "ABC"], ["lead_time_days", "Lead time"],
];

const movementSortOptions = [
  ["date", "Posting Date"],
  ["material", "Material"],
  ["quantity", "Quantity"],
  ["value", "Value"],
  ["document_number", "Document #"],
  ["movement_type", "Movement Type"],
];

const movementTypeOptions = [
  { value: "all", label: "All Movement Types" },
  { value: "inflow", label: "Inflows / Receipts (All)" },
  { value: "outflow", label: "Outflows / Issues (All)" },
  { value: "transfer", label: "Transfers (All)" },
  { value: "101", label: "101 - Goods Receipt (PO)" },
  { value: "102", label: "102 - GR Reversal (PO)" },
  { value: "201", label: "201 - Goods Issue (Cost Center)" },
  { value: "261", label: "261 - Goods Issue (Production Order)" },
  { value: "301", label: "301 - Transfer Plant to Plant" },
  { value: "311", label: "311 - Transfer Storage Location" },
  { value: "321", label: "321 - Transfer QI to Unrestricted" },
  { value: "501", label: "501 - Receipt w/o Purchase Order" },
  { value: "561", label: "561 - Initial Stock Entry" },
  { value: "601", label: "601 - Goods Issue (Delivery/Sales)" },
];

const initialFilters = () => ({
  search: "", plant: "all", storage_location: "all", unit: "all",
  stock_type: "all", status: "all", sort_by: "material", sort_direction: "asc",
});

const defaultDates = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
};

const initialMovementFilters = () => {
  const d = defaultDates();
  return {
    date_preset: "30",
    from_date: d.from,
    to_date: d.to,
    search: "",
    plant: "all",
    storage_location: "all",
    movement_type: "all",
    sort_by: "date",
    sort_direction: "desc",
  };
};

const formatNumber = (value) => (
  Number.isFinite(value) ? value.toLocaleString(undefined, { maximumFractionDigits: 3 }) : "—"
);

const formatValue = (value, currency) => {
  if (!Number.isFinite(value)) return "—";
  if (!currency) return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  try {
    return value.toLocaleString("en-IN", { style: "currency", currency, maximumFractionDigits: 0 });
  } catch {
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency}`;
  }
};

const InventoryTable = () => {
  // View mode: 'stock' (Current Stock Balances) or 'movements' (Stock Movements by Date Range)
  const [inventoryView, setInventoryView] = useState("stock");

  // Current Stock State
  const [draft, setDraft] = useState(initialFilters);
  const [applied, setApplied] = useState(null);
  const [optionsData, setOptionsData] = useState(null);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Stock Movements State (Approach B: Date Filter)
  const [movementsDraft, setMovementsDraft] = useState(initialMovementFilters);
  const [movementsApplied, setMovementsApplied] = useState(null);
  const [movementsData, setMovementsData] = useState(null);
  const [movementsPage, setMovementsPage] = useState(1);
  const [loadingMovements, setLoadingMovements] = useState(false);
  const [exportingMovements, setExportingMovements] = useState(false);

  // Fetch filter options once
  useEffect(() => {
    let active = true;
    const fetchOptions = async () => {
      setLoadingOptions(true);
      try {
        const response = await axios.get(`${API}/supply-chain-agent/inventory/filter-options`);
        if (active) setOptionsData(response.data);
      } catch (error) {
        const detail = error.response?.data?.detail;
        if (active) setOptionsData({
          data_source: "sap_unreachable",
          message: typeof detail === "string" ? detail : "Failed to load live SAP inventory filters",
          filter_options: {},
        });
      } finally {
        if (active) setLoadingOptions(false);
      }
    };
    fetchOptions();
    return () => { active = false; };
  }, []);

  // --- Current Stock Handlers ---
  const requestParams = useMemo(() => {
    if (!applied) return null;
    return {
      search: applied.search.trim() || undefined,
      plant: applied.plant === "all" ? undefined : applied.plant,
      storage_location: applied.storage_location === "all" ? undefined : applied.storage_location,
      unit: applied.unit === "all" ? undefined : applied.unit,
      stock_type: applied.stock_type === "all" ? undefined : applied.stock_type,
      status: applied.status === "all" ? undefined : applied.status,
      sort_by: applied.sort_by,
      sort_direction: applied.sort_direction,
    };
  }, [applied]);

  const fetchInventory = useCallback(async () => {
    if (!requestParams) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/supply-chain-agent/inventory`, {
        params: { ...requestParams, page, page_size: 50 },
      });
      setData(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to load filtered SAP inventory");
      setData({ inventory: [], pagination: { page: 1, pages: 0, total: 0 }, data_source: "sap_unreachable" });
    } finally {
      setLoading(false);
    }
  }, [page, requestParams]);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  const applyFilters = (event) => {
    event.preventDefault();
    const hasFilter = draft.search.trim()
      || [draft.plant, draft.storage_location, draft.unit, draft.stock_type, draft.status].some((value) => value !== "all");
    if (!hasFilter) {
      toast.error("Select at least one inventory filter before loading SAP data");
      return;
    }
    setPage(1);
    setApplied({ ...draft });
  };

  const resetFilters = () => {
    setDraft(initialFilters());
    setApplied(null);
    setData(null);
    setPage(1);
  };

  const exportInventory = async () => {
    if (!requestParams) return;
    setExporting(true);
    try {
      const response = await axios.get(`${API}/supply-chain-agent/inventory/export`, {
        params: requestParams,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "sap_inventory_filtered.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Filtered SAP inventory downloaded");
    } catch {
      toast.error("Filtered inventory export failed");
    } finally {
      setExporting(false);
    }
  };

  // --- Stock Movements (Approach B) Handlers ---
  const handleMovementPresetChange = (preset) => {
    const to = new Date();
    const from = new Date();
    if (preset === "7") {
      from.setDate(from.getDate() - 7);
    } else if (preset === "30") {
      from.setDate(from.getDate() - 30);
    } else if (preset === "90") {
      from.setDate(from.getDate() - 90);
    }
    setMovementsDraft((prev) => ({
      ...prev,
      date_preset: preset,
      from_date: preset === "custom" ? prev.from_date : from.toISOString().slice(0, 10),
      to_date: preset === "custom" ? prev.to_date : to.toISOString().slice(0, 10),
    }));
  };

  const movementsParams = useMemo(() => {
    if (!movementsApplied) return null;
    let mvtType = movementsApplied.movement_type;
    let direction = undefined;
    if (["inflow", "outflow", "transfer"].includes(mvtType)) {
      direction = mvtType;
      mvtType = undefined;
    } else if (mvtType === "all") {
      mvtType = undefined;
    }

    return {
      from_date: movementsApplied.from_date,
      to_date: movementsApplied.to_date,
      search: movementsApplied.search.trim() || undefined,
      plant: movementsApplied.plant === "all" ? undefined : movementsApplied.plant,
      storage_location: movementsApplied.storage_location === "all" ? undefined : movementsApplied.storage_location,
      movement_type: mvtType,
      direction: direction,
      sort_by: movementsApplied.sort_by,
      sort_direction: movementsApplied.sort_direction,
    };
  }, [movementsApplied]);

  const fetchMovements = useCallback(async () => {
    if (!movementsParams) return;
    setLoadingMovements(true);
    try {
      const response = await axios.get(`${API}/supply-chain-agent/stock-movements`, {
        params: { ...movementsParams, page: movementsPage, page_size: 50 },
      });
      setMovementsData(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to load SAP stock movements");
      setMovementsData({ movements: [], summary: {}, pagination: { page: 1, pages: 0, total: 0 }, data_source: "sap_unreachable" });
    } finally {
      setLoadingMovements(false);
    }
  }, [movementsPage, movementsParams]);

  useEffect(() => { fetchMovements(); }, [fetchMovements]);

  const applyMovementsFilters = (event) => {
    event.preventDefault();
    if (!movementsDraft.from_date || !movementsDraft.to_date) {
      toast.error("Select both From Date and To Date");
      return;
    }
    if (movementsDraft.from_date > movementsDraft.to_date) {
      toast.error("From Date cannot be later than To Date");
      return;
    }
    setMovementsPage(1);
    setMovementsApplied({ ...movementsDraft });
  };

  const resetMovementsFilters = () => {
    setMovementsDraft(initialMovementFilters());
    setMovementsApplied(null);
    setMovementsData(null);
    setMovementsPage(1);
  };

  const exportMovements = async () => {
    if (!movementsParams) return;
    setExportingMovements(true);
    try {
      const response = await axios.get(`${API}/supply-chain-agent/stock-movements/export`, {
        params: movementsParams,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `sap_stock_movements_${movementsParams.from_date}_${movementsParams.to_date}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Filtered SAP stock movements downloaded");
    } catch {
      toast.error("Stock movements export failed");
    } finally {
      setExportingMovements(false);
    }
  };

  const options = optionsData?.filter_options || {};
  const rows = data?.inventory || [];
  const pagination = data?.pagination || { page: 1, pages: 0, total: 0 };
  const optionsFailed = optionsData?.data_source && optionsData.data_source !== "sap_live" && optionsData.data_source !== "sap_no_records";
  const noticeSource = (inventoryView === "stock" ? data?.data_source : movementsData?.data_source) || (optionsFailed ? optionsData?.data_source : null);
  const noticeMessage = (inventoryView === "stock" ? data?.message : movementsData?.message) || (optionsFailed ? optionsData?.message : null);

  const mRows = movementsData?.movements || [];
  const mPagination = movementsData?.pagination || { page: 1, pages: 0, total: 0 };
  const mSummary = movementsData?.summary || null;

  return (
    <div className="space-y-3">
      <DataSourceNotice source={noticeSource} message={noticeMessage} showLive />

      {/* Top View Selector Tabs */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-card px-3 py-2 rounded-t-lg">
        <div className="flex items-center gap-1.5 rounded-lg border bg-muted/40 p-1">
          <Button
            type="button"
            variant={inventoryView === "stock" ? "default" : "ghost"}
            size="sm"
            className={`h-7 px-3 text-xs ${
              inventoryView === "stock"
                ? "bg-background text-foreground shadow-sm font-semibold hover:bg-background"
                : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setInventoryView("stock")}
            data-testid="sc-view-stock"
          >
            <Package className="mr-1.5 h-3.5 w-3.5 text-emerald-600" />
            Current Stock Balances
          </Button>
          <Button
            type="button"
            variant={inventoryView === "movements" ? "default" : "ghost"}
            size="sm"
            className={`h-7 px-3 text-xs ${
              inventoryView === "movements"
                ? "bg-background text-foreground shadow-sm font-semibold hover:bg-background"
                : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setInventoryView("movements")}
            data-testid="sc-view-movements"
          >
            <Calendar className="mr-1.5 h-3.5 w-3.5 text-blue-600" />
            Stock Movements (Date Filter)
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {inventoryView === "stock"
            ? "Live instantaneous physical stock balances"
            : "SAP goods movements, receipts, issues & transfers by posting date"}
        </p>
      </div>

      {/* VIEW 1: CURRENT STOCK BALANCES */}
      {inventoryView === "stock" && (
        <Card className="card-industrial" data-testid="sc-inventory-table">
          <CardHeader className="space-y-3 pb-3">
            <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
              <CardTitle className="flex items-center gap-2 text-base">
                <Package className="h-4 w-4 text-emerald-600" />
                {data ? `Inventory Status (${pagination.total} live record${pagination.total === 1 ? "" : "s"})` : "Inventory Status"}
              </CardTitle>
              <p className="text-xs text-muted-foreground">Fresh SAP stock loaded only after filters are applied</p>
            </div>

            <form onSubmit={applyFilters} className="rounded-md border bg-muted/20 p-3" data-testid="sc-inventory-filters">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Filter className="h-3.5 w-3.5" /> SAP filters and sorting
                </span>
                <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={resetFilters} data-testid="sc-inv-reset">
                  <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-[200px] flex-1 max-w-sm">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input placeholder="Material or description..." className="h-9 pl-8 text-sm" value={draft.search} onChange={(event) => setDraft((value) => ({ ...value, search: event.target.value }))} data-testid="sc-inv-search" />
                </div>
                <Select value={draft.plant} onValueChange={(value) => setDraft((current) => ({ ...current, plant: value, storage_location: "all" }))} disabled={loadingOptions}>
                  <SelectTrigger className="h-9 w-[130px] text-sm" data-testid="sc-inv-plant-filter"><SelectValue placeholder="All plants" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All plants</SelectItem>{(options.plants || []).map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={draft.storage_location} onValueChange={(value) => setDraft((current) => ({ ...current, storage_location: value }))} disabled={loadingOptions}>
                  <SelectTrigger className="h-9 w-[145px] text-sm" data-testid="sc-inv-storage-filter"><SelectValue placeholder="All locations" /></SelectTrigger>
                  <SelectContent position="item-aligned" className="max-h-72">
                    <SelectItem value="all">All locations</SelectItem>
                    {(options.storage_locations || []).map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={draft.unit} onValueChange={(value) => setDraft((current) => ({ ...current, unit: value }))} disabled={loadingOptions}>
                  <SelectTrigger className="h-9 w-[120px] text-sm" data-testid="sc-inv-unit-filter"><SelectValue placeholder="All units" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All units</SelectItem>{(options.units || []).map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={draft.stock_type} onValueChange={(value) => setDraft((current) => ({ ...current, stock_type: value }))} disabled={loadingOptions || optionsData?.supports_stock_type === false}>
                  <SelectTrigger className="h-9 w-[150px] text-sm" data-testid="sc-inv-stock-type-filter"><SelectValue placeholder="All stock types" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All stock types</SelectItem>{(options.stock_types || []).map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={draft.status} onValueChange={(value) => setDraft((current) => ({ ...current, status: value }))}>
                  <SelectTrigger className="h-9 w-[135px] text-sm" data-testid="sc-inv-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All statuses</SelectItem>{Object.entries(statusLabels).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
                </Select>
                <div className="flex items-center gap-1.5">
                  <Select value={draft.sort_by} onValueChange={(value) => setDraft((current) => ({ ...current, sort_by: value }))}>
                    <SelectTrigger className="h-9 w-[140px] text-sm" data-testid="sc-inv-sort"><SelectValue placeholder="Sort by" /></SelectTrigger>
                    <SelectContent>{sortOptions.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={draft.sort_direction} onValueChange={(value) => setDraft((current) => ({ ...current, sort_direction: value }))}>
                    <SelectTrigger className="h-9 w-[80px] text-sm" data-testid="sc-inv-sort-direction"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="asc">A–Z</SelectItem><SelectItem value="desc">Z–A</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span>Current stock returns instantaneous balances. To filter by date range:</span>
                  <button
                    type="button"
                    onClick={() => setInventoryView("movements")}
                    className="font-semibold text-blue-600 hover:underline flex items-center gap-0.5 cursor-pointer"
                  >
                    <Calendar className="h-3 w-3" /> Switch to Stock Movements
                  </button>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="h-9 bg-emerald-600 hover:bg-emerald-700" disabled={loading || loadingOptions} data-testid="sc-inv-go">
                    {loading && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} GO
                  </Button>
                  <Button type="button" variant="outline" className="h-9" onClick={exportInventory} disabled={!applied || loading || exporting}>
                    {exporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Export
                  </Button>
                </div>
              </div>
            </form>
          </CardHeader>
          <CardContent className="p-0">
            {!applied ? (
              <div className="flex h-44 flex-col items-center justify-center text-center text-muted-foreground">
                <Filter className="mb-2 h-8 w-8" />
                <p className="font-medium">Apply a filter to load live SAP inventory</p>
                <p className="text-xs">No inventory records are loaded into this table before you press GO.</p>
              </div>
            ) : loading ? (
              <div className="flex h-44 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-emerald-600" /></div>
            ) : rows.length === 0 ? (
              <div className="flex h-44 flex-col items-center justify-center text-center text-muted-foreground">
                <Package className="mb-2 h-8 w-8" />
                <p className="font-medium">No SAP inventory records match the selected filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1280px] text-sm">
                  <thead><tr className="border-b bg-muted/50">
                    <th className="p-3 text-left font-medium">Material</th><th className="p-3 text-left font-medium">Description</th>
                    <th className="p-3 text-left font-medium">Type</th><th className="p-3 text-left font-medium">Plant</th>
                    <th className="p-3 text-left font-medium">Storage Location</th><th className="p-3 text-left font-medium">Stock Type</th>
                    <th className="p-3 text-center font-medium">ABC</th><th className="p-3 text-right font-medium">Qty</th>
                    <th className="p-3 text-left font-medium">Base Unit</th><th className="p-3 text-right font-medium">Reorder Pt</th>
                    <th className="p-3 text-right font-medium">Value</th><th className="p-3 text-center font-medium">Status</th>
                    <th className="p-3 text-right font-medium">Lead Time</th>
                  </tr></thead>
                  <tbody>{rows.map((item, index) => (
                    <tr key={`${item.material}-${item.plant}-${item.storage_location}-${item.stock_type}-${index}`} className={`border-b hover:bg-muted/30 ${item.status === "critical" ? "bg-red-50/50" : ""}`} data-testid={`sc-inv-row-${index}`}>
                      <td className="p-3 font-mono text-xs">{item.material || "—"}</td><td className="p-3">{item.description || "—"}</td>
                      <td className="p-3"><Badge variant="outline" className="text-xs">{item.material_type || item.type || "—"}</Badge></td>
                      <td className="p-3 font-mono text-xs">{item.plant || "—"}</td><td className="p-3 font-mono text-xs">{item.storage_location || "—"}</td>
                      <td className="p-3">{item.stock_type || "—"}</td>
                      <td className="p-3 text-center"><Badge variant="secondary" className={`text-xs ${item.abc_classification === "A" ? "bg-amber-100 text-amber-900" : item.abc_classification === "B" ? "bg-blue-100 text-blue-900" : "bg-gray-100 text-gray-700"}`}>{item.abc_classification ? `Cat ${item.abc_classification}` : "—"}</Badge></td>
                      <td className="p-3 text-right font-medium">{formatNumber(item.quantity)}</td><td className="p-3">{item.unit || "—"}</td>
                      <td className="p-3 text-right">{formatNumber(item.reorder_point)}</td><td className="p-3 text-right">{formatValue(item.stock_value, item.currency)}</td>
                      <td className="p-3 text-center"><Badge className={`text-xs ${statusColors[item.status] || statusColors.unclassified}`}>{item.status === "critical" && <AlertTriangle className="mr-1 h-3 w-3" />}{statusLabels[item.status] || "Not classified"}</Badge></td>
                      <td className="p-3 text-right">{Number.isFinite(item.lead_time_days) ? `${formatNumber(item.lead_time_days)}d` : "—"}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            {applied && !loading && (
              <div className="flex items-center justify-between border-t px-3 py-2 text-sm text-muted-foreground">
                <span>{pagination.total || 0} live SAP record{pagination.total === 1 ? "" : "s"}</span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</Button>
                  <span>Page {pagination.pages ? pagination.page : 0} of {pagination.pages || 0}</span>
                  <Button variant="outline" size="sm" disabled={page >= (pagination.pages || 0)} onClick={() => setPage((value) => value + 1)}>Next</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* VIEW 2: STOCK MOVEMENTS & TRANSACTIONS (APPROACH B: DATE FILTER) */}
      {inventoryView === "movements" && (
        <Card className="card-industrial" data-testid="sc-movements-table">
          <CardHeader className="space-y-3 pb-3">
            <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
              <CardTitle className="flex items-center gap-2 text-base">
                <Calendar className="h-4 w-4 text-blue-600" />
                {movementsData
                  ? `Stock Movements & Transactions (${mPagination.total} record${mPagination.total === 1 ? "" : "s"})`
                  : "Stock Movements & Goods Transactions"}
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Filter material inflows, outflows, and transfers across any posting date range
              </p>
            </div>

            <form onSubmit={applyMovementsFilters} className="rounded-md border bg-muted/20 p-3" data-testid="sc-movements-filters">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Filter className="h-3.5 w-3.5" /> Date range & movement filters
                </span>
                <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={resetMovementsFilters} data-testid="sc-mov-reset">
                  <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {/* Date Preset */}
                <Select value={movementsDraft.date_preset} onValueChange={handleMovementPresetChange}>
                  <SelectTrigger className="h-9 w-[140px] text-sm" data-testid="sc-mov-preset">
                    <Calendar className="mr-1.5 h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <SelectValue placeholder="Period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 Days</SelectItem>
                    <SelectItem value="30">Last 30 Days</SelectItem>
                    <SelectItem value="90">Last 90 Days</SelectItem>
                    <SelectItem value="custom">Custom Date Range</SelectItem>
                  </SelectContent>
                </Select>

                {/* From Date */}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-muted-foreground font-medium">From:</span>
                  <Input
                    type="date"
                    className="h-9 w-[135px] text-xs bg-background"
                    value={movementsDraft.from_date}
                    onChange={(e) => setMovementsDraft((prev) => ({ ...prev, from_date: e.target.value, date_preset: "custom" }))}
                    data-testid="sc-mov-from-date"
                  />
                </div>

                {/* To Date */}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-muted-foreground font-medium">To:</span>
                  <Input
                    type="date"
                    className="h-9 w-[135px] text-xs bg-background"
                    value={movementsDraft.to_date}
                    onChange={(e) => setMovementsDraft((prev) => ({ ...prev, to_date: e.target.value, date_preset: "custom" }))}
                    data-testid="sc-mov-to-date"
                  />
                </div>

                {/* Search */}
                <div className="relative min-w-[180px] flex-1 max-w-xs">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Material, doc #, or text..."
                    className="h-9 pl-8 text-sm"
                    value={movementsDraft.search}
                    onChange={(e) => setMovementsDraft((prev) => ({ ...prev, search: e.target.value }))}
                    data-testid="sc-mov-search"
                  />
                </div>

                {/* Plant */}
                <Select
                  value={movementsDraft.plant}
                  onValueChange={(val) => setMovementsDraft((prev) => ({ ...prev, plant: val, storage_location: "all" }))}
                  disabled={loadingOptions}
                >
                  <SelectTrigger className="h-9 w-[125px] text-sm" data-testid="sc-mov-plant">
                    <SelectValue placeholder="All plants" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All plants</SelectItem>
                    {(options.plants || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>

                {/* Storage Location */}
                <Select
                  value={movementsDraft.storage_location}
                  onValueChange={(val) => setMovementsDraft((prev) => ({ ...prev, storage_location: val }))}
                  disabled={loadingOptions}
                >
                  <SelectTrigger className="h-9 w-[135px] text-sm" data-testid="sc-mov-sloc">
                    <SelectValue placeholder="All locations" />
                  </SelectTrigger>
                  <SelectContent position="item-aligned" className="max-h-72">
                    <SelectItem value="all">All locations</SelectItem>
                    {(options.storage_locations || []).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>

                {/* Movement Type */}
                <Select
                  value={movementsDraft.movement_type}
                  onValueChange={(val) => setMovementsDraft((prev) => ({ ...prev, movement_type: val }))}
                >
                  <SelectTrigger className="h-9 w-[170px] text-sm" data-testid="sc-mov-type">
                    <SelectValue placeholder="Movement type" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {movementTypeOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Sorting */}
                <div className="flex items-center gap-1.5">
                  <Select value={movementsDraft.sort_by} onValueChange={(val) => setMovementsDraft((prev) => ({ ...prev, sort_by: val }))}>
                    <SelectTrigger className="h-9 w-[130px] text-sm" data-testid="sc-mov-sort">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      {movementSortOptions.map(([val, label]) => <SelectItem key={val} value={val}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={movementsDraft.sort_direction} onValueChange={(val) => setMovementsDraft((prev) => ({ ...prev, sort_direction: val }))}>
                    <SelectTrigger className="h-9 w-[80px] text-sm" data-testid="sc-mov-sort-direction"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desc">Newest</SelectItem>
                      <SelectItem value="asc">Oldest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-[11px] text-muted-foreground">
                  Queries SAP Goods Movements (<code>A_MaterialDocumentItem</code>) by posting date range with transaction details.
                </p>
                <div className="flex gap-2">
                  <Button type="submit" className="h-9 bg-blue-600 hover:bg-blue-700 text-white" disabled={loadingMovements} data-testid="sc-mov-go">
                    {loadingMovements && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} GO
                  </Button>
                  <Button type="button" variant="outline" className="h-9" onClick={exportMovements} disabled={!movementsApplied || loadingMovements || exportingMovements} data-testid="sc-mov-export">
                    {exportingMovements ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Export
                  </Button>
                </div>
              </div>
            </form>

            {/* KPI Summary Cards for selected Date Range */}
            {mSummary && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 pt-1">
                <div className="rounded-lg border bg-card p-3 shadow-xs">
                  <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">Total Movements</span>
                  <p className="text-xl font-bold">{mSummary.total_records.toLocaleString()}</p>
                  <span className="text-[10px] text-muted-foreground">{mSummary.unique_documents} documents / {mSummary.unique_materials} materials</span>
                </div>
                <div className="rounded-lg border bg-emerald-50/50 border-emerald-200/60 p-3 shadow-xs">
                  <span className="flex items-center gap-1 text-[11px] font-semibold tracking-wider text-emerald-700 uppercase">
                    <ArrowUpRight className="h-3.5 w-3.5 text-emerald-600" /> Total Receipts (Inflow)
                  </span>
                  <p className="text-xl font-bold text-emerald-800">+{formatNumber(mSummary.total_inflow_qty)}</p>
                  <span className="text-[10px] text-emerald-600/80">Goods receipts & initial entries</span>
                </div>
                <div className="rounded-lg border bg-rose-50/50 border-rose-200/60 p-3 shadow-xs">
                  <span className="flex items-center gap-1 text-[11px] font-semibold tracking-wider text-rose-700 uppercase">
                    <ArrowDownLeft className="h-3.5 w-3.5 text-rose-600" /> Total Issues (Outflow)
                  </span>
                  <p className="text-xl font-bold text-rose-800">-{formatNumber(mSummary.total_outflow_qty)}</p>
                  <span className="text-[10px] text-rose-600/80">Cost center, production & sales</span>
                </div>
                <div className="rounded-lg border bg-blue-50/50 border-blue-200/60 p-3 shadow-xs">
                  <span className="text-[11px] font-semibold tracking-wider text-blue-700 uppercase">Transaction Value</span>
                  <p className="text-xl font-bold text-blue-800">{formatValue(mSummary.total_value, "INR")}</p>
                  <span className="text-[10px] text-blue-600/80">Total recorded valuation amount</span>
                </div>
              </div>
            )}
          </CardHeader>

          <CardContent className="p-0">
            {!movementsApplied ? (
              <div className="flex h-48 flex-col items-center justify-center text-center text-muted-foreground">
                <Calendar className="mb-2 h-8 w-8 text-blue-500" />
                <p className="font-medium">Select a date range and press GO to load stock movements</p>
                <p className="text-xs">Inspect all stock receipts, issues, consumption, and transfers from SAP.</p>
              </div>
            ) : loadingMovements ? (
              <div className="flex h-48 items-center justify-center">
                <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
              </div>
            ) : mRows.length === 0 ? (
              <div className="flex h-48 flex-col items-center justify-center text-center text-muted-foreground">
                <Package className="mb-2 h-8 w-8" />
                <p className="font-medium">No SAP stock movement records found for the selected date range</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1280px] text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="p-3 text-left font-medium">Posting Date</th>
                      <th className="p-3 text-left font-medium">Material Doc #</th>
                      <th className="p-3 text-left font-medium">Material</th>
                      <th className="p-3 text-left font-medium">Description</th>
                      <th className="p-3 text-left font-medium">Plant</th>
                      <th className="p-3 text-left font-medium">SLoc</th>
                      <th className="p-3 text-center font-medium">Mvt Type</th>
                      <th className="p-3 text-center font-medium">Direction</th>
                      <th className="p-3 text-right font-medium">Quantity</th>
                      <th className="p-3 text-left font-medium">Unit</th>
                      <th className="p-3 text-right font-medium">Value</th>
                      <th className="p-3 text-left font-medium">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mRows.map((item, idx) => {
                      const isReceipt = item.direction === "inflow";
                      const isIssue = item.direction === "outflow";
                      const isTransfer = item.direction === "transfer";

                      return (
                        <tr
                          key={`${item.document_number}-${item.document_item}-${idx}`}
                          className="border-b hover:bg-muted/30"
                          data-testid={`sc-mov-row-${idx}`}
                        >
                          <td className="p-3 font-mono text-xs whitespace-nowrap">{item.date || "—"}</td>
                          <td className="p-3 font-mono text-xs whitespace-nowrap">
                            <span className="font-semibold text-foreground">{item.document_number}</span>
                            {item.document_item ? <span className="text-muted-foreground">/{item.document_item}</span> : null}
                          </td>
                          <td className="p-3 font-mono text-xs font-medium">{item.material || "—"}</td>
                          <td className="p-3 max-w-[220px] truncate" title={item.description}>{item.description || "—"}</td>
                          <td className="p-3 font-mono text-xs">{item.plant || "—"}</td>
                          <td className="p-3 font-mono text-xs">{item.storage_location || "—"}</td>
                          <td className="p-3 text-center whitespace-nowrap">
                            <Badge
                              variant="outline"
                              className={`text-xs ${
                                isReceipt
                                  ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                  : isIssue
                                  ? "bg-rose-50 text-rose-800 border-rose-200"
                                  : "bg-blue-50 text-blue-800 border-blue-200"
                              }`}
                            >
                              {item.movement_desc || item.movement_type}
                            </Badge>
                          </td>
                          <td className="p-3 text-center whitespace-nowrap">
                            {isReceipt ? (
                              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[11px] gap-0.5">
                                <ArrowUpRight className="h-3 w-3" /> Inflow
                              </Badge>
                            ) : isIssue ? (
                              <Badge className="bg-rose-100 text-rose-800 border-rose-300 text-[11px] gap-0.5">
                                <ArrowDownLeft className="h-3 w-3" /> Outflow
                              </Badge>
                            ) : (
                              <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-[11px] gap-0.5">
                                <ArrowLeftRight className="h-3 w-3" /> Transfer
                              </Badge>
                            )}
                          </td>
                          <td className={`p-3 text-right font-medium ${isReceipt ? "text-emerald-700" : isIssue ? "text-rose-700" : ""}`}>
                            {isReceipt ? "+" : isIssue ? "-" : ""}{formatNumber(item.quantity)}
                          </td>
                          <td className="p-3 text-xs">{item.unit || "—"}</td>
                          <td className="p-3 text-right font-mono text-xs">{formatValue(item.value, item.currency)}</td>
                          <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                            {item.reference ? <span className="font-mono">{item.reference}</span> : (item.header_text || "—")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {movementsApplied && !loadingMovements && (
              <div className="flex items-center justify-between border-t px-3 py-2 text-sm text-muted-foreground">
                <span>{mPagination.total || 0} live SAP movement{mPagination.total === 1 ? "" : "s"}</span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={movementsPage <= 1} onClick={() => setMovementsPage((v) => v - 1)}>
                    Previous
                  </Button>
                  <span>Page {mPagination.pages ? mPagination.page : 0} of {mPagination.pages || 0}</span>
                  <Button variant="outline" size="sm" disabled={movementsPage >= (mPagination.pages || 0)} onClick={() => setMovementsPage((v) => v + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default InventoryTable;
